import React from "react";
import classes from "./record.module.scss";

export interface JumpTarget {
    /** The target Panel's `id`. */
    id: string;
    label: string;
}

const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

interface JumpLinksProps {
    items: JumpTarget[];
    label?: string;
}

/** "Jump to: Services · Contact · Missions" — scrolls to a panel on the page. */
export const JumpLinks: React.FC<JumpLinksProps> = ({ items, label = "Jump to" }) => {
    if (items.length === 0) return null;
    return (
        <nav className={classes.jump} aria-label={label}>
            <span>{label}</span>
            {items.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    className={`btn-unstyled ${classes.jumpLink}`}
                    title={`Scroll to ${item.label}`}
                    onClick={() => scrollToSection(item.id)}
                >
                    {item.label}
                </button>
            ))}
        </nav>
    );
};

export type SectionState = "filled" | "changed" | "empty" | "error";

export interface SectionIndexItem extends JumpTarget {
    state?: SectionState;
}

const STATE_TEXT: Record<SectionState, string> = {
    filled: "filled in",
    changed: "has unsaved changes",
    empty: "empty",
    error: "needs a fix",
};

const dotClass = (state: SectionState | undefined): string => {
    switch (state) {
        case "filled": return classes.sectionDotFilled;
        case "changed": return classes.sectionDotChanged;
        case "error": return classes.sectionDotError;
        default: return "";
    }
};

interface SectionIndexProps {
    items: SectionIndexItem[];
    activeId?: string;
    title?: string;
}

/**
 * The edit-mode section list: every form section with a dot for its state
 * (filled · changed · empty · error). Clicking scrolls to the section.
 */
export const SectionIndex: React.FC<SectionIndexProps> = ({ items, activeId, title = "Sections" }) => (
    <nav className={classes.sectionIndex} aria-label={title}>
        <span className={classes.sectionIndexTitle}>{title}</span>
        {items.map((item) => (
            <button
                key={item.id}
                type="button"
                className={`btn-unstyled ${classes.sectionItem} ${item.id === activeId ? classes.sectionItemActive : ""}`}
                title={`${item.label}${item.state ? ` — ${STATE_TEXT[item.state]}` : ""}`}
                aria-current={item.id === activeId ? "true" : undefined}
                onClick={() => scrollToSection(item.id)}
            >
                <span>{item.label}</span>
                <span className={`${classes.sectionDot} ${dotClass(item.state)}`} aria-hidden="true" />
            </button>
        ))}
    </nav>
);

export default JumpLinks;
